import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  autoUpdate,
  computePosition as computePositionBase,
  limitShift,
  offset as computePositionOffset,
  Placement,
  shift,
} from "@floating-ui/dom";
import cn from "classnames";
import { APP_CONTAINER_NODE_CLASSNAME } from "shared/constants/common.constants";
import { AppDOM } from "shared/utils/app.utils";
import styles from "./Tooltip.module.scss";

type PortalProps = {
  children?: ReactNode;
  className: string;
};

const Portal: RFC<PortalProps> = ({ children, className }) => {
  const [nodeElement] = useState(document.createElement("div"));

  useEffect(() => {
    const appContainer = AppDOM.querySelector<HTMLDivElement>(`.${APP_CONTAINER_NODE_CLASSNAME}`);
    appContainer?.appendChild(nodeElement);
    if (className) {
      nodeElement.className = className;
    }
    return () => nodeElement?.remove();
  }, []);

  return ReactDOM.createPortal(children, nodeElement);
};

export type TooltipProps = {
  className?: string;
  portalClassName?: string;
  triggerElement: ReactNode;
  placement?: Placement;
  offsetY: number;
  offsetX?: number;
  enabledOnMobile?: boolean;
  disabled?: boolean;
};

export const Tooltip: RFC<TooltipProps> = ({
  triggerElement,
  placement = "top",
  className,
  portalClassName,
  children,
  offsetY,
  offsetX = 0,
  enabledOnMobile = false,
  disabled = false,
}) => {
  const triggerRef = useRef<null | HTMLDivElement>(null);
  const tooltipRef = useRef<null | HTMLDivElement>(null);
  const [canShowTooltip, setCanShowTooltip] = useState(false);
  const [calculatedPos, setCalculatedPos] = useState({ left: -5000, top: 0 });

  const portalInlineStyles: CSSProperties = {
    transform: `translate(${calculatedPos.left + offsetX}px, ${calculatedPos.top}px)`,
  };

  const updatePosition = () => {
    const middleware = [
      computePositionOffset({ mainAxis: offsetY }),
      shift({
        limiter: limitShift({
          crossAxis: false,
        }),
      }),
    ];
    if (triggerRef.current && tooltipRef.current) {
      computePositionBase(triggerRef.current, tooltipRef.current, {
        placement,
        middleware,
      }).then(({ x, y }) => {
        setCalculatedPos({ top: y, left: x });
      });
    }
  };

  const resetPosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      autoUpdate(triggerRef.current, tooltipRef.current, updatePosition);
    }
  };

  useLayoutEffect(() => {
    updatePosition();
  }, []);

  useEffect(() => {
    if (!canShowTooltip) return;
    updatePosition();
  }, [canShowTooltip]);

  const showTooltipHandler = () => {
    if (disabled) return;
    setCanShowTooltip(true);
  };

  const hideTooltipHandler = () => {
    setCanShowTooltip(false);
    resetPosition();
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={cn(styles.tooltip, className)}
        onMouseEnter={showTooltipHandler}
        onMouseLeave={hideTooltipHandler}
      >
        {triggerElement}
      </div>
      {canShowTooltip && (
        <Portal className={cn(styles.portalWrap, portalClassName, { [styles.enabledOnMobile]: enabledOnMobile })}>
          <div style={portalInlineStyles} ref={tooltipRef}>
            {children}
          </div>
        </Portal>
      )}
    </>
  );
};
